import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';

import type { DragEvent, MouseEvent, KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import type { SortableHandleProps } from './SortableHandle';
import type { SortableItemColors } from 'components/Sortable/Sortable';

import { Icon } from 'components/Icon';
import { SortableHandle } from 'components/Sortable/components/SortableHandle';
import styles from 'components/Sortable/Sortable.module.css';
import { useSortable } from 'components/Sortable/SortableProvider';

export interface SortableItemProps {
  children: ReactNode;
  handlePlacement: SortableHandleProps['placement'];
  disabled?: boolean;
  sortableItemColor: SortableItemColors;
  index: number;
}

const ACCESS_KEYS = ['Space', 'ArrowDown', 'ArrowUp'];
const noSupportTouch = 'ontouchstart' in globalThis.document.documentElement;
const dragImage = new Image(0, 0);

dragImage.src =
  // 1px transparent png
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==';

export function SortableItem({ children, handlePlacement, disabled, sortableItemColor, index }: SortableItemProps) {
  const {
    clone,
    dragEnd,
    draggingSource,
    draggingTarget,
    dragStart,
    drop,
    maxIndex,
    moveToPosition,
    setClone,
    setDraggingSource,
  } = useSortable();
  const [grabbing, setGrabbing] = useState(false);
  const [moving, setmoving] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);
  const hasIndex = index !== undefined && index >= 0;

  useEffect(() => {
    if (!itemRef.current) return;
    if (globalThis.document.activeElement === itemRef.current) return;

    if (index === draggingTarget && draggingTarget === draggingSource) {
      // Manually setting focus after an item has been moved with the keyboard; focus is lost after rerender
      itemRef.current.focus();
      setmoving(true);
    } else {
      setmoving(false);
    }
  }, [index, draggingTarget, draggingSource, setDraggingSource]);

  function handleDragStart(event: DragEvent<HTMLDivElement>) {
    event.stopPropagation();

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setDragImage(dragImage, 0, 0);

    if (itemRef.current) {
      const cloned = itemRef.current.cloneNode(true) as Element;

      cloned.classList.add(styles['sortable__sort-item--ghost']);

      setClone(cloned);
    }

    if (hasIndex) dragStart(index);
    setGrabbing(true);
  }

  function handleDragEnd() {
    if (grabbing) return;

    if (clone) document.body.removeChild(clone);

    setGrabbing(false);
    dragEnd();
  }

  function handleKeyUp(event: ReactKeyboardEvent<HTMLDivElement>) {
    const { code } = event;

    if (code === 'Tab' && draggingSource !== undefined) {
      dragEnd();
      return;
    }

    if (ACCESS_KEYS.includes(code) === false) return;

    if (!hasIndex) return;

    if (code === 'Space') {
      if (moving) {
        setmoving(false);
        dragEnd();
      } else {
        setmoving(true);
        dragStart(index);
      }

      return;
    }

    if (draggingSource === undefined) return;

    const newTarget = code === 'ArrowUp' ? index - 1 : index + 1;

    if (newTarget < 0 || newTarget > maxIndex) return;

    moveToPosition(newTarget);
  }

  function handleMove(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();

    if (!hasIndex) return;

    dragStart(index);

    const { direction } = event.currentTarget.dataset;
    const newTarget = direction === 'up' ? index - 1 : index + 1;

    drop(newTarget);
  }

  return (
    <div
      className={clsx(styles['sortable__sort-item'], styles[`sortable__sort-item--${sortableItemColor}`])}
      draggable={!disabled}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      onKeyUpCapture={handleKeyUp}
      ref={itemRef}
      role="presentation"
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
    >
      <div
        className={`${styles['sortable__sort-item__contents']} ${styles[`sortable__sort-item__contents--handle-${handlePlacement}`]}`}
      >
        {handlePlacement === 'left' && <SortableHandle placement="left" />}
        {children}
        {handlePlacement === 'right' && <SortableHandle placement="right" />}
      </div>

      <div className={styles['sortable__sort-item__button-container']} data-touch={noSupportTouch}>
        {!disabled && hasIndex && (
          <>
            {hasIndex && (
              <>
                {index > 0 && (
                  <button
                    className={`${styles['sortable__sort-item__handle']} ${styles['sortable__sort-item__handle--up']}`}
                    data-direction="up"
                    onClickCapture={handleMove}
                  >
                    <div className={styles['sortable__sort-item__handle--icon']}>
                      <Icon name="CheveronDown" />
                    </div>
                    <span className={styles['sortable__visually-hidden']}>Move up</span>
                  </button>
                )}

                {index < maxIndex && (
                  <button
                    className={`${styles['sortable__sort-item__handle']} ${styles['sortable__sort-item__handle--down']}`}
                    data-direction="down"
                    onClickCapture={handleMove}
                  >
                    <div className={styles['sortable__sort-item__handle--icon']}>
                      <Icon name="CheveronDown" />
                    </div>
                    <span className={styles['sortable__visually-hidden']}>Move down</span>
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
